import { Badge, Button, Tooltip, Typography } from "antd"
import { memo } from "react"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import { EVALUATION_STATUS } from "#customTypes/composedTypes/evaluationTypes"
import { Uuid } from "#customTypes/globalTypes"

import { StatusCounts } from "./useEvaluationWorkloadDashboard"

const { Text } = Typography

const SHoveredSegment = styled.span<{
  width: number
  color: string
  textColor: string
  minWidth?: number
  isActive?: boolean
  isInteractive?: boolean
}>`
  width: ${({ width }) => `${width}%`};
  min-width: ${({ minWidth = 18 }) => `${minWidth}px`};
  background-color: ${({ color }) => color};
  transition:
    width 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
    min-width 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
    transform 0.18s ease,
    box-shadow 0.18s ease,
    filter 0.18s ease,
    border-color 0.18s ease;
  border: 1px solid transparent;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ textColor }) => textColor};
  font-size: 12px;
  font-weight: 500;
  position: relative;
  z-index: 0;
  pointer-events: ${({ isInteractive }) => (isInteractive ? "auto" : "none")};
  border-color: ${({ isActive }) =>
    isActive ? "rgba(15, 23, 42, 0.55)" : "transparent"};
  box-shadow: ${({ isActive }) =>
    isActive ? "inset 0 0 0 2px rgba(255,255,255,0.6)" : "none"};

  &:hover {
    border-color: rgba(31, 41, 55, 0.35);
    transform: translateY(-1px);
    z-index: 2;
    box-shadow:
      inset 0 0 0 2px rgba(255, 255, 255, 0.7),
      0 2px 6px rgba(15, 23, 42, 0.14);
    filter: saturate(1.35);
  }
`

export const STATUS_SEGMENTS = [
  {
    status: EVALUATION_STATUS.NOT_STARTED,
    label: "Not Started",
    color: "#d9d9d9",
    textColor: "#1f2937",
  },
  {
    status: EVALUATION_STATUS.IN_PROGRESS,
    label: "In Progress",
    color: "#faad14",
    textColor: "#1f2937",
  },
  {
    status: EVALUATION_STATUS.SUBMITTED,
    label: "Submitted",
    color: "#fa8c16",
    textColor: "#ffffff",
  },
  {
    status: EVALUATION_STATUS.REJECTED,
    label: "Rejected",
    color: "#ff4d4f",
    textColor: "#ffffff",
  },
  {
    status: EVALUATION_STATUS.APPROVED,
    label: "Approved",
    color: "#52c41a",
    textColor: "#ffffff",
  },
  {
    status: EVALUATION_STATUS.PRINTED,
    label: "Printed",
    color: "#1677ff",
    textColor: "#ffffff",
  },
] as const

type EvaluationWorkloadProgressRowProps = {
  personId: Uuid
  personName: string
  total: number
  completeCount: number
  statusCounts: StatusCounts
  maxTotal: number
  isActive: boolean
  isFullyComplete: boolean
  statusFilters: string[]
  onToggleFilter: (personId: Uuid) => void
  onStatusSegmentClick: (status: EVALUATION_STATUS) => void
  badgeOverflowCount: number
  segmentKeyPrefix: string
  barTooltipTitle?: string
}

const EvaluationWorkloadProgressRow = ({
  personId,
  personName,
  total,
  completeCount,
  statusCounts,
  maxTotal,
  isActive,
  isFullyComplete,
  statusFilters,
  onToggleFilter,
  onStatusSegmentClick,
  badgeOverflowCount,
  segmentKeyPrefix,
  barTooltipTitle,
}: EvaluationWorkloadProgressRowProps) => {
  const completionPercent =
    total === 0 ? 0 : Math.round((completeCount / total) * 100)
  const barContent = (
    <Flex
      style={{
        alignItems: "center",
        marginLeft: 4,
        height: 30,
        width: `${Math.max((total / maxTotal) * 520, 40)}px`,
        minWidth: 40,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #eceff3",
        backgroundColor: "#f7f8fa",
      }}
    >
      {STATUS_SEGMENTS.map((segment) => {
        const count = statusCounts[segment.status]
        const hasCount = count > 0 && total > 0
        const widthPercent = hasCount ? (count / total) * 100 : 0

        return (
          <Tooltip
            key={`${segmentKeyPrefix}-${personId}-${segment.status}`}
            title={hasCount ? `${segment.label}: ${count}` : null}
          >
            <SHoveredSegment
              width={widthPercent}
              color={segment.color}
              textColor={segment.textColor}
              minWidth={hasCount ? 18 : 0}
              isInteractive={hasCount}
              isActive={
                hasCount &&
                statusFilters.length === 1 &&
                statusFilters[0] === segment.status
              }
              onClick={() => {
                if (hasCount) onStatusSegmentClick(segment.status)
              }}
              style={{ cursor: hasCount ? "pointer" : "default" }}
            >
              {hasCount ? count : null}
            </SHoveredSegment>
          </Tooltip>
        )
      })}
    </Flex>
  )

  return (
    <Flex
      gap={8}
      style={{
        alignItems: "center",
        borderRadius: 0,
        padding: "2px 4px 0px",
      }}
    >
      <Button
        type={isActive ? "primary" : "default"}
        onClick={() => onToggleFilter(personId)}
        style={{
          width: 130,
          borderRadius: 10,
          boxShadow: "none",
        }}
        icon={isFullyComplete ? "✅" : undefined}
      >
        {personName}
      </Button>
      <Flex
        gap={10}
        style={{
          alignItems: "center",
          width: 720,
          justifyContent: "flex-start",
        }}
      >
        <Badge
          count={total}
          overflowCount={badgeOverflowCount}
          color="#8c8c8c"
          style={{ width: 56, minWidth: 56 }}
        />
        {barTooltipTitle ? (
          <Tooltip title={barTooltipTitle}>{barContent}</Tooltip>
        ) : (
          barContent
        )}
        <Text strong style={{ fontSize: 15, lineHeight: 1.2 }}>
          {completeCount}/{total} ({completionPercent}%)
        </Text>
      </Flex>
    </Flex>
  )
}

export default memo(EvaluationWorkloadProgressRow)
